import indicatorLocalContextSvg from '@disy/cadenza-icons/analysis-context-data-view.svg';
import indicatorAnalysisContextSvg from '@disy/cadenza-icons/analysis-context.svg';
import scaleDependentIcon from '@disy/cadenza-icons/map.svg';
import { icon } from '@disy/lib/icon';

import { getStore } from 'cadenza/store/cadenza-store';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import { isMap } from 'cadenza/workbook/api/data-view-visualization-key';
import { modifyDataViewAttribute } from 'cadenza/workbook/data-view/data-view-utils';
import { isAnalysisContextFilterActive } from 'cadenza/workbook/view/view-filters-button/view-filters-button-settings';
import { getAttributeById, getDataViewById } from 'cadenza/workbook/workbook-core-selectors.state';
import i18n from 'cadenza/workbook/workbook-panel/settings/attributes/data-view-bundle-controller.properties';

export function isHierarchyControlledByMapScale(dataViewId, hierarchy) {
  return (
    isMap(getDataViewById(getStore().getState(), dataViewId).visualizationKey) &&
    hierarchy.usesScaleDependentHierarchyLevel &&
    hierarchy.supportsScaleDependentHierarchyControl
  );
}

/** @return {MenuItemGroup} */
export function createHierarchyControlMenuActions(dataViewId, hierarchy, { disabledFn } = {}) {
  const { id, supportsScaleDependentHierarchyControl } = hierarchy;
  let calculatedDisabledFn = disabledFn;
  if (!isAnalysisContextFilterActive(dataViewId)) {
    calculatedDisabledFn = () => true;
  }
  const items = [
    createHierarchyContextLocalControlMenuAction(dataViewId, id, {
      disabledFn,
      checked: isHierarchyControlledLocally(dataViewId, hierarchy),
    }),
    createHierarchyContextGlobalControlMenuAction(dataViewId, id, {
      disabledFn: calculatedDisabledFn,
      checked: !hierarchy.usesLocalHierarchyLevel,
    }),
  ];
  if (
    isMap(getDataViewById(getStore().getState(), dataViewId).visualizationKey) &&
    supportsScaleDependentHierarchyControl
  ) {
    items.push(
      createHierarchyContextMapScaleControlMenuAction(dataViewId, id, {
        disabledFn,
        checked: isHierarchyControlledByMapScale(dataViewId, hierarchy),
      }),
    );
  }

  return {
    label: i18n('hierarchy.control.menugroup.title'),
    group: items,
    groupType: 'radio',
  };
}

function createHierarchyContextLocalControlMenuAction(
  dataViewId,
  id,
  { disabledFn = () => false, checked = false } = {},
) {
  return {
    label: i18n('hierarchy.control.local'),
    icon: icon(indicatorLocalContextSvg),
    execute: () =>
      changeHierarchyControl(dataViewId, id, [
        [AttributeModificationKey.USE_SCALE_DEPENDENT_HIERARCHY_LEVEL, false],
        [AttributeModificationKey.USE_LOCAL_HIERARCHY_LEVEL, true],
      ]),
    disabled: disabledFn(),
    checked,
  };
}

function createHierarchyContextGlobalControlMenuAction(
  dataViewId,
  id,
  { disabledFn = () => false, checked = false } = {},
) {
  return {
    label: i18n('hierarchy.control.global'),
    icon: icon(indicatorAnalysisContextSvg),
    execute: () =>
      changeHierarchyControl(dataViewId, id, [
        [AttributeModificationKey.USE_SCALE_DEPENDENT_HIERARCHY_LEVEL, false],
        [AttributeModificationKey.USE_LOCAL_HIERARCHY_LEVEL, false],
      ]),
    disabled: disabledFn(),
    checked,
  };
}

function createHierarchyContextMapScaleControlMenuAction(
  dataViewId,
  id,
  { disabledFn = () => false, checked = false } = {},
) {
  return {
    label: i18n('hierarchy.control.map-scale-dependent'),
    icon: icon(scaleDependentIcon, { size: 'xs' }),
    execute: () =>
      changeHierarchyControl(dataViewId, id, [
        [AttributeModificationKey.USE_SCALE_DEPENDENT_HIERARCHY_LEVEL, true],
        [AttributeModificationKey.USE_LOCAL_HIERARCHY_LEVEL, true],
      ]),
    disabled: disabledFn(),
    checked,
  };
}

function changeHierarchyControl(dataViewId, id, modifications) {
  const attributeSettings = getAttributeById(getStore().getState(), id).settings || {};
  modifications = modifications.filter(([modification, value]) => value !== attributeSettings[modification]);
  if (modifications.length) {
    modifyDataViewAttribute(dataViewId, id, modifications);
  }
}

function isHierarchyControlledLocally(dataViewId, hierarchy) {
  return hierarchy.usesLocalHierarchyLevel && !isHierarchyControlledByMapScale(dataViewId, hierarchy);
}
